/**
 * Pagina /superadmin/ewelink — mantenedor de la integracion eWeLink (F4).
 *
 * Contenedor: hace el fetch y delega el render a `PanelEwelink`, que es el
 * mismo componente validado en el preview dev-only `/dev/ewelink`.
 *
 * Doctrina (convención #17):
 * - Solo administra QUE dispositivos puede tocar el portal.
 * - Las credenciales y el modo (`EWELINK_BACKEND`) viven en variables de
 *   entorno; ver `docs/dev/ewelink-variables-entorno.md`.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarDispositivoEwelink,
  getInventarioEwelink,
  sincronizarEwelink,
  type InventarioEwelinkRead,
} from '../../lib/api/ewelink';
import {
  PanelEwelink,
  type DispositivoEwelinkRead,
} from '../../components/superadmin/PanelEwelink';
import { PageHeader } from '../../components/layout/PageHeader';
import { formatearFechaHora } from '../../lib/tiempo';

export function EwelinkPage() {
  const [inventario, setInventario] = useState<InventarioEwelinkRead | null>(
    null,
  );
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setInventario(await getInventarioEwelink());
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : 'No pudimos cargar el inventario de eWeLink.',
      );
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function sincronizar() {
    setCargando(true);
    setError(null);
    setAviso(null);
    try {
      const r = await sincronizarEwelink();
      setInventario(r.inventario);
      const partes = [
        r.creados ? `${r.creados} nuevo(s)` : null,
        r.actualizados ? `${r.actualizados} actualizado(s)` : null,
        r.sin_cambios ? `${r.sin_cambios} sin cambios` : null,
      ].filter(Boolean);
      setAviso(
        partes.length
          ? `Inventario sincronizado: ${partes.join(', ')}.`
          : 'Inventario sincronizado.',
      );
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : 'No pudimos conectar con eWeLink.',
      );
    } finally {
      setCargando(false);
    }
  }

  async function toggle(d: DispositivoEwelinkRead, valor: boolean) {
    setGuardando(d.deviceid);
    setError(null);
    setAviso(null);
    try {
      const actualizado = await actualizarDispositivoEwelink(
        d.deviceid,
        valor,
      );
      setInventario((prev) =>
        prev
          ? {
              ...prev,
              dispositivos: prev.dispositivos.map((x) =>
                x.deviceid === d.deviceid
                  ? { ...x, administrado: actualizado.administrado }
                  : x,
              ),
            }
          : prev,
      );
    } catch (e) {
      // El 409 del backend explica por qué no se puede administrar un
      // dispositivo sin clasificar: se muestra tal cual.
      setError(
        e instanceof ApiError
          ? e.message
          : `No pudimos actualizar ${d.nombre}.`,
      );
    } finally {
      setGuardando(null);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Integraciones"
        title="eWeLink"
        description="Dispositivos de la cuenta del barrio y qué puede operar el portal."
      />

      {error && (
        <div
          role="alert"
          className="rounded-md border border-state-danger bg-state-danger/10 p-3 text-sm text-state-danger"
        >
          {error}
        </div>
      )}
      {aviso && (
        <div className="rounded-md border border-state-success bg-state-success/10 p-3 text-sm text-state-success">
          {aviso}
        </div>
      )}

      {inventario && (
        <PanelEwelink
          estado={{
            ...inventario.estado,
            consultado_at: inventario.estado.consultado_at
              ? formatearFechaHora(inventario.estado.consultado_at)
              : null,
          }}
          dispositivos={inventario.dispositivos}
          onProbarConexion={() => void sincronizar()}
          onToggleAdministrado={(d, v) => void toggle(d, v)}
          cargando={cargando}
          guardando={guardando}
        />
      )}

      {!inventario && cargando && (
        <p className="text-sm text-text-secondary">Cargando…</p>
      )}
    </div>
  );
}
